import React from 'react';
import './ComplaintFeedback.css';

const ComplaintsPieChart = ({ 
  data, 
  totalComplaints, 
  isLoading, 
  error, 
  insights = [], 
  recommendations = [],
  dateRange = 'all' // Add dateRange parameter
}) => {
  
  // Helper function to get display text for date range
  const getDateRangeDisplayText = (range) => {
    const rangeMap = {
      'today': 'Today',
      'yesterday': 'Yesterday', 
      'week': 'Past Week',
      'month': 'Past Month',
      'year': 'Past Year',
      'all': 'All Time'
    };
    return rangeMap[range] || 'All Time';
  };

  if (error) {
    return (
      <div style={{ 
        height: '100%',
        background: '#F8FAFC',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '2px solid #e2e8f0',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: '#ef4444'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>⚠️</div>
          <div style={{ 
            fontSize: '14px', 
            fontWeight: '500', 
            color: '#334155',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}>Error loading complaints</div>
          <div style={{ 
            fontSize: '12px', 
            color: '#64748b', 
            marginTop: '4px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="complaints-loading-container">
        <div className="complaints-loading-content">
          <div className="complaints-loading-spinner"></div>
          <div className="complaints-loading-title">Loading complaints...</div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="complaints-no-data-container">
        <div className="complaints-no-data-content">
          <div className="complaints-no-data-icon">🎉</div>
          <div className="complaints-no-data-title">No complaints found</div>
          <div className="complaints-no-data-subtitle">That's great news!</div>
        </div>
      </div>
    );
  }

  // Use your established feedback color system
  const colors = [
    '#ef4444', // Error Red (for most serious complaints)
    '#fb923c', // Orange (for moderate complaints)
    '#f59e0b', // Amber Orange (for minor complaints)
    '#22c55e', // Success Green (for positive feedback if any)
    '#667eea', // Your brand color
    '#64748b', // Neutral gray
    '#94a3b8', // Light gray
    '#475569'  // Dark gray
  ];

  const total = totalComplaints || data.reduce((sum, item) => sum + item.count, 0);
  
  // Calculate pie chart segments
  let currentAngle = 0;
  const segments = data.map((item, index) => {
    const percentage = (item.count / total) * 100;
    const angle = (item.count / total) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    
    currentAngle += angle;
    
    return {
      ...item,
      percentage,
      startAngle,
      endAngle,
      color: colors[index % colors.length]
    };
  });

  const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  };

  const createPath = (centerX, centerY, radius, startAngle, endAngle) => {
    // Handle full circle case (100% data)
    if (endAngle - startAngle >= 359.9) {
      return `M ${centerX} ${centerY} m -${radius} 0 a ${radius} ${radius} 0 1 1 ${radius * 2} 0 a ${radius} ${radius} 0 1 1 -${radius * 2} 0`;
    }
    
    const start = polarToCartesian(centerX, centerY, radius, endAngle);
    const end = polarToCartesian(centerX, centerY, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    
    return [
      "M", centerX, centerY,
      "L", start.x, start.y,
      "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
      "Z"
    ].join(" ");
  };

  return (
    <div className="complaints-display-container">
      {/* Subtle gradient overlay */}
      <div className="complaints-gradient-overlay"></div>
      
      {/* Header - Updated to show date range */}
      <div className="complaints-header">
        <h3 className="complaints-title">
          Customer Dissatisfaction Factors ({getDateRangeDisplayText(dateRange)})
        </h3>
        <div className="complaints-subtitle">
          Total: {total} complaint{total !== 1 ? 's' : ''}
        </div>
      </div>
      
      <div className="complaints-content">
        {/* Pie Chart */}
        <div className="complaints-chart-section">
          <div className="complaints-chart-container">
            <svg width="140" height="140" viewBox="0 0 140 140">
              {segments.map((segment, index) => (
                <path
                  key={index}
                  d={createPath(70, 70, 60, segment.startAngle, segment.endAngle)}
                  fill={segment.color}
                  stroke="rgba(255, 255, 255, 0.8)"
                  strokeWidth="2"
                  style={{ 
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.05)';
                    e.target.style.transformOrigin = '70px 70px';
                    e.target.style.filter = 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.filter = 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))';
                  }}
                />
              ))}
            </svg>
          </div>
        </div>
        
        {/* Legend */}
        <div className="complaints-legend">
          {segments.map((segment, index) => (
            <div key={index} className="complaints-legend-item">
              <div 
                className="complaints-legend-color"
                style={{ backgroundColor: segment.color }}
              ></div>
              <div className="complaints-legend-text">
                {segment.text}
              </div>
              <div className="complaints-legend-percentage">
                {segment.percentage.toFixed(1)}%
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Insights and Recommendations */}
      {(insights.length > 0 || recommendations.length > 0) && (
        <div className="complaints-insights">
          {insights.length > 0 && (
            <div className="complaints-insight-text">
              📊 {insights[0].text}
            </div>
          )}
          
          {recommendations.length > 0 && (
            <div className="complaints-recommendation">
              💡 {recommendations[0].text}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ComplaintsPieChart;